import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React, {Component} from 'react';
import styled from 'styled-components';

import {Link, maxExtendedWidth, pxToEm} from '../../components/globalStyles';
import {HexagonSize} from '../../components/hexagonIconImage';
import HexagonLeaflet from '../../components/hexagonLeaflet';
import SectionDescription from '../../components/sectionDescription';
import {Product, ProductToAppRoutePath, ProductToHexagonIconSrc, ProductToText} from '../../data/product';
import BitmovinLogoWhitePath from '../../media/bitmovin_logo_white.svg';
import FireworkIconPath from '../../media/firework.svg';

//#region Styled Components

const StyledWrapper = styled.div`
  position: relative;
  margin-top: 4em;
  margin-bottom: ${pxToEm(95)}em;
`;

//#region Background Styled Components

const backgroundBorderRadius = '11px';

const StyledProductsBackground1 = styled.div`
  top: 0;
  left: 5%;
  right: -4%;
  height: 100%;
  position: absolute;
  background: linear-gradient(120.85deg, #001839 0%, #1f467a 98.31%);
  border-radius: ${backgroundBorderRadius};
  transform: perspective(600px) rotateY(4deg);
  transform-origin: center;
  z-index: -1;
  overflow: hidden;

  @media (max-width: ${maxExtendedWidth}) {
    left: 5%;
    right: -2.5%;
  }
`;

const StyledProductsBackground2 = styled.div`
  top: 0;
  left: 31%;
  width: 50%;
  height: 100%;
  position: absolute;
  border-radius: ${backgroundBorderRadius};
  background: linear-gradient(113.26deg, #1faae2 0%, rgba(30, 171, 226, 0) 97.84%);
  transform: perspective(600px) rotateY(10deg);
  z-index: -2;
`;

const StyledProductsBackground3 = styled.div`
  top: 0;
  left: 44%;
  width: 50%;
  height: 100%;
  position: absolute;
  border-radius: ${backgroundBorderRadius};
  background: linear-gradient(114.78deg, #1faae2 0%, rgba(20, 171, 224, 0) 98.31%);
  transform: perspective(600px) rotateY(10deg);
  z-index: -2;
`;

const StyledProductsBackground4 = styled.div`
  top: 0;
  left: 32%;
  width: 50%;
  height: 100%;
  position: absolute;
  border-radius: ${backgroundBorderRadius};
  background: linear-gradient(108.75deg, #1faae2 0%, rgba(20, 171, 224, 0) 98.31%);
  transform: perspective(600px) rotateY(-4deg) rotateZ(180deg);
  z-index: -2;
`;

const StyledProductsBackground5 = styled.div`
  top: 0;
  left: 44%;
  width: 50%;
  height: 100%;
  position: absolute;
  border-radius: ${backgroundBorderRadius};
  background: linear-gradient(109.21deg, #1faae2 0%, rgba(20, 171, 224, 0) 98.31%);
  transform: perspective(600px) rotateY(-4deg) rotateZ(180deg);
  z-index: -2;
`;

const StyledFireworks = styled.img`
  height: 120%;
  top: -10%;
  right: -15%;
  position: absolute;
`;

//#endregion

const StyledProductsContent = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 3em ${pxToEm(92)}em;
`;

const StyledBitmovinLogoImg = styled.img`
  margin-top: ${pxToEm(20)}em;
  width: ${pxToEm(225)}em;
  height: ${pxToEm(94)}em;
`;

const StyledSectionDescription = styled(SectionDescription)`
  margin-top: ${pxToEm(84)}em;
  text-align: left;
  color: white;
  > h1 {
    font-size: ${pxToEm(60)}em;
    line-height: 1.2;
    font-weight: 500;
  }
`;

const StyledParagraph = styled.p`
  font-size: ${pxToEm(22)}em;
  line-height: 1.6;
  color: white;
`;

const StyledProductsList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${pxToEm(181)}em;
  margin-right: ${pxToEm(181)}em;

  > *:not(:last-child) {
    margin-bottom: ${pxToEm(20)}em;
  }
`;

const StyledProductListItemDesc = styled.div`
  display: flex;
  flex-direction: column;
  width: ${pxToEm(400)}em;
  padding: ${pxToEm(7)}em;

  > h3 {
    font-size: ${pxToEm(22)}em;
    color: ${baseVariables.colors.midnight};
    margin: 0 0 ${pxToEm(4)}em;
  }

  > p {
    font-size: ${pxToEm(16)}em;
    margin: 0;
    line-height: 1.2;
  }
`;

//#endregion

class ProductsSection extends Component {
  public render() {
    const hexagonIconOverflowPx = 20;

    return (
      <StyledWrapper>
        <StyledProductsBackground1>
          <StyledFireworks src={FireworkIconPath} />
        </StyledProductsBackground1>
        <StyledProductsBackground2 />
        <StyledProductsBackground3 />
        <StyledProductsBackground4 />
        <StyledProductsBackground5 />

        <StyledProductsContent>
          <div>
            <StyledBitmovinLogoImg src={BitmovinLogoWhitePath} alt="Bitmovin Logo" />
            <StyledSectionDescription headlineType={1} headline="Shaping the Future of Video" />
            <StyledParagraph>
              Build streaming experiences and deliver the highest quality video faster and on more devices than ever
              before.
            </StyledParagraph>
          </div>

          <StyledProductsList>
            <Link to={ProductToAppRoutePath[Product.Encoding]}>
              <HexagonLeaflet
                hexagonIconSrc={ProductToHexagonIconSrc[Product.Encoding]}
                hexagonSize={HexagonSize.Big}
                hexagonOverflowPx={hexagonIconOverflowPx}>
                <StyledProductListItemDesc>
                  <h3>Next-Generation VOD Encoder</h3>
                  <p>Deliver content in the highest quality. Prepare content at high speed and the lowest bitrate.</p>
                </StyledProductListItemDesc>
              </HexagonLeaflet>
            </Link>

            <Link to={ProductToAppRoutePath[Product.LiveEncoding]}>
              <HexagonLeaflet
                hexagonIconSrc={ProductToHexagonIconSrc[Product.LiveEncoding]}
                hexagonSize={HexagonSize.Big}
                hexagonOverflowPx={hexagonIconOverflowPx}>
                <StyledProductListItemDesc>
                  <h3>Live Encoding</h3>
                  <p>Deliver live streams in the highest quality.</p>
                </StyledProductListItemDesc>
              </HexagonLeaflet>
            </Link>

            <Link to={ProductToAppRoutePath[Product.Playback]}>
              <HexagonLeaflet
                hexagonIconSrc={ProductToHexagonIconSrc[Product.Player]}
                hexagonSize={HexagonSize.Big}
                hexagonOverflowPx={hexagonIconOverflowPx}>
                <StyledProductListItemDesc>
                  <h3>{ProductToText[Product.Playback]}</h3>
                  <p>Deploy, test &amp; optimize to provide flawless video playback.</p>
                </StyledProductListItemDesc>
              </HexagonLeaflet>
            </Link>
          </StyledProductsList>
        </StyledProductsContent>
      </StyledWrapper>
    );
  }
}

export default ProductsSection;
